import React, { useEffect, useState, useRef } from 'react';
import api from '../../utils/api';

const QRCodeTab = () => {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expiresIn, setExpiresIn] = useState('');
  const [qrExpiry, setQrExpiry] = useState(null);
  const printRef = useRef(null);

  const generateQR = async () => {
    setLoading(true);
    try {
      const body = {};
      if (expiresIn) body.expiresIn = Number(expiresIn);
      const { data } = await api.post('/attendance/qr/generate', body);
      setQrData(data);
      setQrExpiry(data.expiresAt ? new Date(data.expiresAt) : null);
    } catch (err) {
      console.error('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generateQR();
  }, []);

  useEffect(() => {
    if (!qrExpiry) return;
    const interval = setInterval(() => {
      const remaining = qrExpiry - Date.now();
      if (remaining <= 0) {
        setQrData(null);
        setQrExpiry(null);
        clearInterval(interval);
      }
      setQrExpiry(prev => new Date(prev));
    }, 1000);
    return () => clearInterval(interval);
  }, [qrExpiry]);

  const formatCountdown = () => {
    if (!qrExpiry) return '';
    const diff = Math.max(0, Math.floor((qrExpiry - Date.now()) / 1000));
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - Attendance</title>
          <style>
            body { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
            img { width: 400px; height: 400px; }
            h1 { font-size: 28px; margin-bottom: 8px; color: #111; }
            p { font-size: 16px; color: #666; margin-top: 0; }
            .date { font-size: 14px; color: #999; margin-top: 16px; }
          </style>
        </head>
        <body>
          <h1>${qrData?.studioName || 'Studio'}</h1>
          <p>Scan to mark your attendance</p>
          <img src="${qrData?.qrCode}" alt="QR Code" />
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  return (
    <div>
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center" ref={printRef}>
          {qrData ? (
            <>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">{qrData.studioName}</h3>
              <p className="text-sm text-gray-500 mb-6">Students scan this QR code to mark attendance</p>
              <img src={qrData.qrCode} alt="QR Code" className="mx-auto mb-6 w-72 h-72" />

              {qrExpiry ? (
                <div className="mb-6">
                  <span className="text-sm text-gray-500">Expires in </span>
                  <span className={`text-sm font-semibold ${Math.floor((qrExpiry - Date.now()) / 1000) < 300 ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatCountdown()}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-6">Valid for today</p>
              )}

              <div className="flex items-center justify-center gap-3 mb-4">
                <select
                  value={expiresIn}
                  onChange={e => setExpiresIn(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-400"
                >
                  <option value="">No expiry</option>
                  <option value="900">15 minutes</option>
                  <option value="1800">30 minutes</option>
                  <option value="3600">1 hour</option>
                  <option value="7200">2 hours</option>
                </select>
                <button
                  onClick={generateQR}
                  disabled={loading}
                  className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
                >
                  {loading ? 'Generating...' : 'Regenerate'}
                </button>
              </div>

              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z" />
                </svg>
                Print QR Code
              </button>
            </>
          ) : loading ? (
            <div className="py-12">
              <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Generating QR code...</p>
            </div>
          ) : (
            <div className="py-8">
              <p className="text-gray-500 mb-4">QR code has expired</p>
              <select
                value={expiresIn}
                onChange={e => setExpiresIn(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 mb-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                <option value="">No expiry</option>
                <option value="900">15 minutes</option>
                <option value="1800">30 minutes</option>
                <option value="3600">1 hour</option>
                <option value="7200">2 hours</option>
              </select>
              <br />
              <button
                onClick={generateQR}
                disabled={loading}
                className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors mt-2"
              >
                Generate New QR Code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeTab;
